import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import FolderApi from '@/api/folder'
import i18nPresenter from '@/i18nPresenter'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

const SET_SHARED_PRESENTATIONS = 'SET_SHARED_PRESENTATIONS'
const SET_SHARED_FOLDERS = 'SET_SHARED_FOLDERS'
const SET_OWNED_PRESENTATIONS = 'SET_OWNED_PRESENTATIONS'
const SET_OWNED_FOLDERS = 'SET_OWNED_FOLDERS'
const SET_LOADING_SHARED_PRESENTATIONS = 'SET_LOADING_SHARED_PRESENTATIONS'
const SET_LOADING_SHARED_FOLDERS = 'SET_LOADING_SHARED_FOLDERS'
const SET_LOADING_OWNED_PRESENTATIONS = 'SET_LOADING_OWNED_PRESENTATIONS'
const SET_LOADING_OWNED_FOLDERS = 'SET_LOADING_OWNED_FOLDERS'
const SET_PRESENTATION_NO_PERMISSION = 'SET_PRESENTATION_NO_PERMISSION'

const EDITABLE_PERMISSION = 'edit-presentation'

const _state = {
  sharedPresentations: [],
  sharedFolders: [],
  ownedPresentations: [],
  ownedFolders: [],
  isLoadingSharedPresentations: false,
  isLoadingSharedFolders: false,
  isLoadingOwnedPresentations: false,
  isLoadingOwnedFolders: false,
  requestAccessPresentationId: null,
}

const _getters = {
  canUseGetFullFolderTreeV2(_, __, ___, rootGetters) {
    return isFeatureEnabled(rootGetters, 'release-get-full-folder-tree-v2')
  },
  sharedPresentations(state) {
    return state.sharedPresentations
  },
  sharedFolders(state) {
    return state.sharedFolders
  },
  ownedPresentations(state) {
    return state.ownedPresentations
  },
  ownedFolders(state) {
    return state.ownedFolders
  },
  presentations(state) {
    return [...state.sharedPresentations, ...state.ownedPresentations]
  },
  requestAccessPresentationId(state) {
    return state.requestAccessPresentationId
  },
}

const actions = {
  async getFullFolderTree({ getters }) {
    return getters.canUseGetFullFolderTreeV2
      ? FolderApi.getFullFolderTreeV2()
      : FolderApi.getFullFolderTree()
  },
  async getSharedPresentationsAction({ commit, rootGetters }) {
    commit(SET_LOADING_SHARED_PRESENTATIONS, true)
    try {
      const { presentations } = await PresentationApi.getSharedPresentations({
        limit: 500,
        includedAllFolders: true,
        permission: EDITABLE_PERMISSION,
        ...(rootGetters['multipleSessions/canInvitePresenterToFolder'] ? { batchShareAsPresenter: true } : {}),
      })
      commit(SET_SHARED_PRESENTATIONS, presentations)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      commit(SET_LOADING_SHARED_PRESENTATIONS, false)
    }
  },
  async getSharedFoldersAction({ commit }) {
    commit(SET_LOADING_SHARED_FOLDERS, true)
    try {
      const folders = await FolderApi.getShareFolders({
        allLevel: true,
      })
      commit(SET_SHARED_FOLDERS, [{ folderId: null, folderName: i18nPresenter.t('Shared with Me') }, ...folders])
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      commit(SET_LOADING_SHARED_FOLDERS, false)
    }
  },
  async getOwnedPresentationsAction({ commit }) {
    commit(SET_LOADING_OWNED_PRESENTATIONS, true)
    try {
      const data = await PresentationApi.getOwnedPresentations()
      commit(SET_OWNED_PRESENTATIONS, data)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      commit(SET_LOADING_OWNED_PRESENTATIONS, false)
    }
  },
  async getOwnedFoldersAction({ commit }) {
    commit(SET_LOADING_OWNED_FOLDERS, true)
    try {
      const data = await FolderApi.getOwnedFolders()
      commit(SET_OWNED_FOLDERS, data)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      commit(SET_LOADING_OWNED_FOLDERS, false)
    }
  },
  async setPresentationNoPermission({ commit }, requestAccessPresentationId) {
    commit(SET_PRESENTATION_NO_PERMISSION, requestAccessPresentationId)
  },
}

const mutations = {
  [SET_SHARED_PRESENTATIONS](state, sharedPresentations) {
    state.sharedPresentations = sharedPresentations
  },
  [SET_SHARED_FOLDERS](state, sharedFolders) {
    state.sharedFolders = sharedFolders
  },
  [SET_OWNED_PRESENTATIONS](state, ownedPresentations) {
    state.ownedPresentations = ownedPresentations
  },
  [SET_OWNED_FOLDERS](state, ownedFolders) {
    state.ownedFolders = ownedFolders
  },
  [SET_LOADING_SHARED_PRESENTATIONS](state, isLoading) {
    state.isLoadingSharedPresentations = isLoading
  },
  [SET_LOADING_SHARED_FOLDERS](state, isLoading) {
    state.isLoadingSharedFolders = isLoading
  },
  [SET_LOADING_OWNED_PRESENTATIONS](state, isLoading) {
    state.isLoadingOwnedPresentations = isLoading
  },
  [SET_LOADING_OWNED_FOLDERS](state, isLoading) {
    state.isLoadingOwnedFolders = isLoading
  },
  [SET_PRESENTATION_NO_PERMISSION](state, requestAccessPresentationId) {
    state.requestAccessPresentationId = requestAccessPresentationId
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
